// Shared parts of the finance pages (M5-04): month pickers, labels, the caller's finance standing
// (`financeAccess.list` `me`), the notices for a restricted page and for read-only finance, the
// org chart's names, money input and file download. Composes @companyos/ui only.
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Toolbar,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { EyeOffIcon, LockIcon } from "lucide-react";
import type { ComponentType, FormEvent, ReactNode } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { ChartBody, ChartFrame, holderNames, setupCrumb } from "../chart/chart-parts";
import { orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { COST_KIND_LABELS } from "../format";
import { problemText } from "../ops-parts";
import { SetupDescription } from "../settings/setup-description";
import { financeAccessQuery } from "./finance-queries";

/**
 * A finance screen (Chart UX pass 2026-10-21; Mosaic, Runway, Brex budgets): the 56 px location
 * header (Reports › Finance › title, or Setup › Finance › title, with the actions on the right),
 * an optional 40 px toolbar (the period controls), then the body with its gutters. No eyebrow,
 * no 28 px heading, no description paragraph.
 */
export function FinancePage({
  company,
  where,
  title,
  icon,
  count,
  actions,
  toolbar,
  screen,
  children,
}: {
  company: CompanyAccess;
  where: "chart" | "setup";
  title: string;
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  count?: number;
  actions?: ReactNode;
  toolbar?: ReactNode;
  /** `data-screen` of the frame (the visual suite and screenshots find it). */
  screen?: string;
  children: ReactNode;
}) {
  return (
    <ChartFrame data-screen={screen ?? `finance-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <LocationHeader
        // KPIS-MODE: the finance views live in Reports (`where` "chart" is their old name); the
        // top bar's trail (KPIs › Reports › page) already says so, so they add no second one.
        {...(where === "setup" ? { crumbs: [setupCrumb(company), { label: "Finance" }] } : {})}
        {...(icon ? { icon } : {})}
        title={title}
        {...(count !== undefined ? { count } : {})}
        {...(actions ? { actions } : {})}
        // SETUP-REGROUP: Setup › Finance pages say what they are for (UX-A: behind an (i)).
        {...(where === "setup" ? { badge: <SetupDescription /> } : {})}
      />
      {toolbar ? (
        <Toolbar
          bleed
          aria-label={`${title} tools`}
          data-slot="finance-toolbar"
          className="max-md:h-auto max-md:flex-wrap max-md:gap-y-1.5 max-md:py-1.5"
        >
          {toolbar}
        </Toolbar>
      ) : null}
      <ChartBody>{children}</ChartBody>
    </ChartFrame>
  );
}

export const CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "NZD",
  "CHF",
  "SGD",
  "PHP",
  "MXN",
  "INR",
  "ZAR",
] as const;
export type Currency = (typeof CURRENCIES)[number];

/** Ledger kinds plus what the finance views accrue (pay, benefits, recurring expenses). */
export const FINANCE_KIND_LABELS: Record<string, string> = {
  ...COST_KIND_LABELS,
  human_comp: "Pay",
  benefit: "Benefits",
  overhead: "Overhead",
  software_subscription: "Software",
};

export const HOLDER_LABELS = { person: "Person", digital_employee: "Digital employee" } as const;

/** `YYYY-MM` of a date (UTC). */
export function monthKeyOf(d: Date): string {
  return d.toISOString().slice(0, 7);
}

/** `count` months back from `from` (newest first), plus `ahead` months after it. */
export function monthsAround(from: string, back = 12, ahead = 0): string[] {
  const [y = 2026, m = 1] = from.split("-").map(Number);
  return Array.from({ length: back + ahead + 1 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 + ahead - i, 1));
    return monthKeyOf(d);
  });
}

export function monthLabel(key: string): string {
  return new Date(`${key}-15T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "2026-Q4" / "2026" / "2026-10" → words. */
export function periodLabel(key: string): string {
  if (/^\d{4}-\d{2}$/.test(key)) return monthLabel(key);
  const q = /^(\d{4})-Q([1-4])$/.exec(key);
  if (q) return `Q${q[2]} ${q[1]}`;
  return key;
}

/** A decimal ratio ("0.4250") as a percent ("42.5%", "2056%" from a hundred up); null → "—". */
export function ratioText(decimal: string | null): string {
  if (decimal === null) return "—";
  const pct = Number(decimal) * 100;
  if (Number.isInteger(pct)) return `${pct}%`;
  return `${Math.abs(pct) >= 100 ? Math.round(pct) : pct.toFixed(1)}%`;
}

export function MonthSelect({
  value,
  onChange,
  months,
  label = "Month",
  id,
}: {
  value: string;
  onChange: (month: string) => void;
  months: readonly string[];
  label?: string;
  id?: string;
}) {
  const options = months.includes(value) ? months : [value, ...months];
  return (
    <NativeSelect
      {...(id ? { id } : { "aria-label": label })}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((m) => (
        <NativeSelectOption key={m} value={m}>
          {monthLabel(m)}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

export function CurrencySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (currency: Currency) => void;
}) {
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value as Currency)}>
      {CURRENCIES.map((c) => (
        <NativeSelectOption key={c} value={c}>
          {c}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

/** The caller's finance standing: who may read restricted rows and who may write. */
export function useFinanceStanding(api: Api, company: CompanyAccess) {
  const q = useQuery(financeAccessQuery(api, company.id));
  const me = q.data?.me;
  return {
    query: q,
    loaded: q.isSuccess,
    owner: me?.owner ?? false,
    admin: me?.finance_admin ?? false,
    /** Margins, scenarios, the plan and exports: the owner and finance admins. */
    restricted: Boolean(me && (me.owner || me.finance_admin)),
    compSeats: new Set(me?.comp_viewer_seat_ids ?? []),
  };
}

const accessLink = (company: CompanyAccess) => (
  <Link
    to="/c/$slug/setup/finance"
    params={{ slug: company.slug }}
    search={keepFocus}
    className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
  >
    Finance access
  </Link>
);

/** A page whose rows are the owner's and finance admins' only. */
export function RestrictedNotice({ company, what }: { company: CompanyAccess; what: string }) {
  return (
    <Notice
      tone="info"
      icon={LockIcon}
      data-slot="finance-restricted"
      title={`${what} are restricted`}
      action={accessLink(company)}
    >
      Only the owner and finance admins see them, because they carry pay and revenue. The owner
      grants finance admin in Setup › Finance › Access.
    </Notice>
  );
}

/** Finance writes need `finance_admin`; the owner reads but must grant it to themselves first. */
export function ReadOnlyNotice({ company, owner }: { company: CompanyAccess; owner: boolean }) {
  return (
    <Notice
      tone="neutral"
      icon={LockIcon}
      data-slot="finance-read-only"
      title="Read-only: changes need finance admin"
      action={owner ? accessLink(company) : null}
    >
      {owner
        ? "You own the company, so you read every figure. To change them, grant yourself finance admin in Setup › Finance › Access."
        : "Ask the company owner for finance admin."}
    </Notice>
  );
}

/** "Pay hidden" on a seat whose compensation and benefits the caller may not read. */
export function PayHiddenBadge() {
  return (
    <Badge
      variant="outline"
      className="gap-1"
      data-slot="pay-hidden"
      title="Pay and benefits are left out of this seat: you may not read them"
    >
      <EyeOffIcon aria-hidden="true" className="size-3" />
      Pay hidden
    </Badge>
  );
}

/** Seat titles, team names and roles from the org chart the chart and sidebar already hold. */
export function useOrgNames(api: Api, company: CompanyAccess) {
  const chart = useQuery(orgChartQuery(api, company.id));
  const seats = chart.data?.seats ?? [];
  const teams = chart.data?.teams ?? [];
  return {
    chart,
    seats,
    teams,
    /** The seat row of the org chart (its holders), for the avatar and the person's name. */
    seatOf: (id: string) => seats.find((s) => s.id === id),
    /** "Jordan Lee" for a held seat, "" for an open one or an unknown id. */
    holderOf: (id: string) => {
      const s = seats.find((x) => x.id === id);
      return s ? holderNames(s) : "";
    },
    seatName: (id: string | null) =>
      id ? (seats.find((s) => s.id === id)?.title ?? `Seat …${id.slice(-6)}`) : "—",
    teamName: (id: string | null) =>
      id ? (teams.find((t) => t.id === id)?.name ?? `Team …${id.slice(-6)}`) : "—",
    nodeName: (id: string) =>
      id === company.id
        ? company.name
        : (seats.find((s) => s.id === id)?.title ??
          teams.find((t) => t.id === id)?.name ??
          `…${id.slice(-6)}`),
  };
}

/**
 * A form in a dialog (every finance create and edit): the caller owns the fields and the
 * mutation; the dialog submits, shows the problem, and closes on success (`open` is the
 * caller's, so it can close it from `onSuccess`).
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  pending,
  error,
  canSubmit = true,
  onSubmit,
  wide = false,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  submitLabel: string;
  pending: boolean;
  /** The failed mutation's error (shown as its problem's detail). */
  error: unknown;
  canSubmit?: boolean;
  onSubmit: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit && !pending) onSubmit();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={wide ? "sm:max-w-2xl" : undefined}>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          <FieldGroup>
            {children}
            {error ? <FieldError>{problemText(error, "That did not work.")}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!canSubmit || pending}>
              {pending ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A labelled field (label above the control, an optional hint or error below). */
export function FormField({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <Field data-invalid={error ? true : undefined}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {children}
      {error ? (
        <FieldError>{error}</FieldError>
      ) : hint ? (
        <FieldDescription>{hint}</FieldDescription>
      ) : null}
    </Field>
  );
}

/** Saves a file the API sent (an export) under `name`. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
